import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { REVEAL_MS, type Track, albumScore, revealBars, revealProgress } from "@trackmark/core";
import { RatingStrip } from "../design/RatingStrip.js";
import { type HistoryEvent, type MbRecording, type RecordingOption, RowMenu } from "./RowMenu.js";
import { type PullPreview, type ReleaseOption, TrackListPull } from "./TrackListPull.js";
import styles from "./Album.module.css";

export interface AlbumTrack {
  id: number;
  no: number | null;
  title: string;
  durationMs: number | null;
  isCore: boolean;
  rating: number | null;
  /** Set when this track is an appearance of a recording whose definitive copy is elsewhere. */
  linkedTo?: number | null;
  /** How many OTHER albums hold this recording. */
  alsoOn?: number;
  /** A rated song elsewhere in the library this one collapses onto. */
  sameAs?: { title: string; rating: number } | null;
}

export interface AlbumEdition {
  id: number;
  name: string;
  trackCount: number;
}

export interface AlbumProps {
  /**
   * Open with this track under the cursor — `/album/7?song=91`, which is where the library's song
   * titles point. A song has no page of its own; it has a place on a record, and this is it.
   */
  focusSongId?: number;
  title: string;
  artist: string;
  year: number | null;
  label: string | null;
  genre: string | null;
  art: string | null;
  tracks: AlbumTrack[];
  editions?: AlbumEdition[];
  /** null = unset, which is deliberately distinct from 0.0. */
  cohesion?: number | null;
  onRate?: (songId: number, value: number) => void;
  /** The stepper was a useState — a number the user set, watched work, and lost on reload. */
  onCohesion?: (cohesion: number | null) => void;
  onEnterBooth?: () => void;

  /**
   * The album's own state. `claimed && !tracked` is the record the user said they own at import
   * and whose track list has not been pulled yet — the one state that puts the pull band on this
   * page. See docs/IMPORT-PROPOSAL.md §8.
   */
  albumId?: number;
  isTracked?: boolean;
  claimed?: boolean;

  onToggleCore?: (songId: number, isCore: boolean) => void;
  loadHistory?: (songId: number) => Promise<HistoryEvent[]>;
  loadRecordings?: (songId: number) => Promise<RecordingOption[]>;
  /** The wider MusicBrainz recording search behind the row menu's re-link (§D6). */
  searchRecordings?: (q: string) => Promise<MbRecording[]>;
  onPointAt?: (songId: number, recordingMbid: string) => void;

  loadReleases?: (albumId: number) => Promise<{ releases: ReleaseOption[]; needsChoice: boolean }>;
  loadPullPreview?: (albumId: number, releaseMbid: string) => Promise<PullPreview>;
  onPull?: (albumId: number, releaseMbid: string) => Promise<void>;
}

const duration = (ms: number | null) =>
  ms === null ? "—" : `${Math.floor(ms / 60000)}:${String(Math.floor((ms % 60000) / 1000)).padStart(2, "0")}`;

/**
 * The album page. Two columns: metadata and the score block on the left, the track grid on the
 * right — which is the app's second rating surface, not just a readout. Arrow keys move a row
 * cursor and the number keys rate in place.
 */
export function Album({
  title,
  artist,
  year,
  label,
  genre,
  art,
  tracks: initialTracks,
  editions = [],
  cohesion: initialCohesion = null,
  onRate,
  onCohesion,
  onEnterBooth,
  albumId,
  focusSongId,
  isTracked = true,
  claimed = false,
  onToggleCore,
  loadHistory,
  loadRecordings,
  searchRecordings,
  onPointAt,
  loadReleases,
  loadPullPreview,
  onPull,
}: AlbumProps) {
  const [tracks, setTracks] = useState(initialTracks);
  const [cohesion, setCohesionState] = useState<number | null>(initialCohesion);

  /**
   * Optimistic, like every other write on this page: the score block recomputes from `cohesion`,
   * and waiting on the network would make the number the user just nudged arrive late.
   */
  const setCohesion = useCallback(
    (next: (current: number | null) => number | null) => {
      setCohesionState((current) => {
        const value = next(current);
        onCohesion?.(value);
        return value;
      });
    },
    [onCohesion],
  );
  const [cursor, setCursor] = useState(0);
  const [hover, setHover] = useState<{ id: number; value: number } | null>(null);
  const [dismissedTrays, setDismissedTrays] = useState<number[]>([]);
  const [editionNotice, setEditionNotice] = useState<string | null>(null);

  const core = useMemo(() => tracks.filter((t) => t.isCore), [tracks]);
  const bonus = useMemo(() => tracks.filter((t) => !t.isCore), [tracks]);
  const ordered = useMemo(() => [...core, ...bonus], [core, bonus]);

  /**
   * The cursor twice: state for the paint, a ref for the keyboard.
   *
   * The handler is a closure over the render that installed it, and React has not necessarily
   * re-rendered between two keystrokes typed at speed. Reading `cursor` there means ↓↓↓9 can rate
   * the row the cursor was on three keys ago — the wrong song, silently, on the fastest and most
   * ordinary way to use this page.
   */
  const cursorRef = useRef(0);
  /** The row the cursor is on, so an arrival from elsewhere can be scrolled to. */
  const cursorRow = useRef<HTMLDivElement>(null);
  const moveCursor = useCallback(
    (delta: number) => {
      const next = Math.max(0, Math.min(ordered.length - 1, cursorRef.current + delta));
      cursorRef.current = next;
      setCursor(next);
    },
    [ordered.length],
  );

  /*
   * Arriving from the library's song title: put the cursor on that track and scroll it into view,
   * once.
   *
   * Once, because this is where you LAND, not where you are pinned — the arrow keys own the
   * cursor from the first keystroke, and a re-run would drag the page back to the track you came
   * in on while you were reading a different one. `AlbumScreen` remounts per record, so the flag
   * cannot leak between two albums.
   */
  const landed = useRef(false);
  useEffect(() => {
    if (focusSongId === undefined || landed.current) return;
    const index = ordered.findIndex((t) => t.id === focusSongId);
    if (index < 0) return;
    landed.current = true;
    cursorRef.current = index;
    setCursor(index);
  }, [focusSongId, ordered]);

  const scrolled = useRef(false);
  useEffect(() => {
    if (!landed.current || scrolled.current || !cursorRow.current) return;
    scrolled.current = true;
    // `center`, not `start`: a track two rows from the end of a long record would otherwise sit
    // at the top of a scroller that cannot scroll that far, and appear not to have moved.
    cursorRow.current.scrollIntoView({ block: "center" });
  }, [cursor]);

  const scoreInput: Track[] = useMemo(
    () => tracks.map((t) => ({ rating: t.rating, isCore: t.isCore, durationMs: t.durationMs })),
    [tracks],
  );
  const result = useMemo(() => albumScore(scoreInput, cohesion), [scoreInput, cohesion]);

  // ---- the score reveal: 400ms, once, when the final core track is rated ----
  const [reveal, setReveal] = useState(0);
  const revealed = useRef(false);
  const wasComplete = useRef(result.complete);

  useEffect(() => {
    if (!result.complete || wasComplete.current || revealed.current) {
      wasComplete.current = result.complete;
      return;
    }
    wasComplete.current = true;
    revealed.current = true;

    const reduced =
      typeof globalThis.matchMedia === "function" &&
      globalThis.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) {
      setReveal(1);
      return;
    }

    const start = performance.now();
    let raf = 0;
    const tick = () => {
      const p = revealProgress(performance.now() - start);
      setReveal(p);
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [result.complete]);

  // A revisit shows the finished state immediately; the reveal never replays.
  const revealT = result.complete ? (revealed.current ? reveal : 1) : 0;
  const settled = !result.complete || revealT > 0.98;

  const rate = useCallback(
    (songId: number, value: number) => {
      setTracks((prev) => prev.map((t) => (t.id === songId ? { ...t, rating: value } : t)));
      setHover(null);
      onRate?.(songId, value);
    },
    [onRate],
  );

  const onKey = useCallback(
    (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (target && (target.tagName === "INPUT" || target.tagName === "SELECT" || target.tagName === "TEXTAREA")) return;

      if (event.key === "ArrowDown") {
        event.preventDefault();
        moveCursor(1);
      } else if (event.key === "ArrowUp") {
        event.preventDefault();
        moveCursor(-1);
      } else if (/^[0-9]$/.test(event.key)) {
        const track = ordered[cursorRef.current];
        if (!track) return;
        event.preventDefault();
        rate(track.id, event.key === "0" ? 10 : Number(event.key));
      }
    },
    [moveCursor, ordered, rate],
  );

  /**
   * Bound before paint, not after.
   *
   * `useEffect` is passive: React defers it past the paint, so there is a window in which the
   * track grid is on screen, the page says ↑ ↓ MOVE CURSOR — and the keys do nothing, because
   * nothing is listening yet. On a loaded machine that window is long enough to lose a burst of
   * keystrokes, which on a rating surface means silently dropping ratings.
   */
  useLayoutEffect(() => {
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onKey]);

  const tray = ordered.find((t) => t.rating === null && t.sameAs && !dismissedTrays.includes(t.id));
  const unratedCore = core.filter((t) => t.rating === null).length;

  const barsVisible = result.complete ? revealBars(revealT, tracks.length) : tracks.length;
  const shownScore =
    result.score === null ? "—" : (result.score * (result.complete ? revealT : 1)).toFixed(2);

  return (
    <div className={styles.page}>
      <div className={styles.left}>
        {art ? (
          <img className={styles.art} src={art} alt="" />
        ) : (
          <div className={`${styles.art} ${styles.artFallback}`}>{title}</div>
        )}
        <div className={styles.title}>{title}</div>
        <div className={styles.artist}>{artist}</div>
        <div className={styles.meta}>{[year, label, genre].filter(Boolean).join(" · ").toUpperCase()}</div>

        <div className={styles.scoreBlock} data-complete={result.complete && settled ? "true" : "false"} data-testid="score-block">
          <div className={styles.eyebrow}>{result.complete ? "COMPLETE" : "INCOMPLETE"}</div>
          <div className={styles.score} data-testid="album-score">
            {shownScore}
          </div>

          <div className={styles.spark}>
            {tracks.map((t, i) => (
              <div
                key={t.id}
                className={styles.bar}
                data-unrated={t.rating === null ? "true" : undefined}
                data-bonus={!t.isCore ? "true" : undefined}
                style={{ height: `${t.rating ? 8 + t.rating * 3.4 : 10}px`, opacity: i < barsVisible ? undefined : 0 }}
              />
            ))}
          </div>

          <div className={styles.lines}>
            {result.complete ? (
              <>
                {result.basis === "duration-weighted"
                  ? `DURATION-WEIGHTED · ${core.length} / ${core.length} CORE`
                  : `UNWEIGHTED · NO DURATIONS ON FILE · ${core.length} / ${core.length} CORE`}
                <br />
                UNWEIGHTED {result.unweighted?.toFixed(2)}
                {cohesion !== null && ` · COHESION ${cohesion > 0 ? "+" : ""}${cohesion.toFixed(1)}`}
                {result.preClamp !== null && (
                  <>
                    {" "}
                    <abbr title={`Cohesion would have made this ${result.preClamp.toFixed(2)}; the scale stops at 10.00.`}>
                      CLAMPED
                    </abbr>
                  </>
                )}
              </>
            ) : (
              <>
                {core.length - unratedCore} / {core.length} CORE TRACKS RATED
                <br />
                NO PROVISIONAL SCORE IS SHOWN
              </>
            )}
          </div>
        </div>

        <div className={styles.control}>
          <span className={styles.controlLabel}>Adjust for cohesion</span>
          <div className={styles.stepper}>
            <button type="button" aria-label="Decrease cohesion" onClick={() => setCohesion((c) => clampStep((c ?? 0) - 0.1))}>
              −
            </button>
            <div className={styles.stepperValue} data-unset={cohesion === null ? "true" : undefined} data-testid="cohesion">
              {cohesion === null ? "—" : `${cohesion > 0 ? "+" : ""}${cohesion.toFixed(1)}`}
            </div>
            <button type="button" aria-label="Increase cohesion" onClick={() => setCohesion((c) => clampStep((c ?? 0) + 0.1))}>
              +
            </button>
          </div>
        </div>

        {editions.length > 1 && (
          <div className={styles.control}>
            <span className={styles.controlLabel}>Edition</span>
            <select
              className={styles.select}
              aria-label="Edition"
              onChange={(e) => {
                const next = editions.find((x) => String(x.id) === e.target.value);
                if (!next) return;
                const delta = next.trackCount - tracks.length;
                setEditionNotice(
                  `Switching to ${next.name} ${delta >= 0 ? "adds" : "removes"} ${Math.abs(delta)} tracks. Your ratings are unaffected.`,
                );
              }}
            >
              {editions.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.name}
                </option>
              ))}
            </select>
            {editionNotice && <div className={styles.confirm}>{editionNotice}</div>}
          </div>
        )}
      </div>

      <div className={styles.right}>
        <div className={styles.gridHead}>
          <div className={styles.gridTitle}>Tracks</div>
          <div className={styles.gridNote}>↑ ↓ MOVE CURSOR · 1–0 RATE IN PLACE</div>
        </div>

        {/* A claimed album with no track list is the one thing that outranks the tray: without it
            there is no complete record to be rating toward. */}
        {claimed && !isTracked && albumId !== undefined && loadReleases && loadPullPreview && onPull && (
          <TrackListPull
            albumId={albumId}
            ownedCount={tracks.length}
            loadReleases={loadReleases}
            loadPreview={loadPullPreview}
            onApply={onPull}
          />
        )}

        {tray && (
          <div className={styles.tray} data-testid="same-as-tray">
            <div className={styles.trayCopy}>
              <b>{tray.title}</b> looks like <b>{tray.sameAs!.title}</b>, which you rated {tray.sameAs!.rating}.
            </div>
            <button type="button" className={styles.trayPrimary} onClick={() => rate(tray.id, tray.sameAs!.rating)}>
              Use the same rating
            </button>
            <button type="button" className={styles.trayText} onClick={() => setDismissedTrays((d) => [...d, tray.id])}>
              Keep separate
            </button>
            <button
              type="button"
              className={styles.trayClose}
              aria-label="Dismiss"
              onClick={() => setDismissedTrays((d) => [...d, tray.id])}
            >
              ×
            </button>
          </div>
        )}

        <div className={styles.well}>
          <div className={styles.rowHead}>
            <div>#</div>
            <div>TITLE</div>
            <div>RATING</div>
            <div style={{ textAlign: "right" }}>SCR</div>
            {/* Its own class, not the row cell's: this one sits on the dark well and inherits
                the head's on-well grey, while the cell below sits on a light row. */}
            <div className={styles.headDur} style={{ textAlign: "right" }}>
              DUR
            </div>
            <div className={styles.menuCell} />
          </div>

          <div className={styles.rows}>
            {ordered.map((t, i) => {
              const isCursor = i === cursor;
              const state = isCursor ? "cursor" : t.rating === null && t.isCore ? "unrated" : undefined;
              return (
                <div key={t.id}>
                  {i === core.length && <div className={styles.bonusDivider}>BONUS</div>}
                  <div
                    className={styles.row}
                    ref={isCursor ? cursorRow : undefined}
                    data-state={state}
                    data-bonus={!t.isCore ? "true" : undefined}
                  >
                    <div className={styles.num}>{t.isCore ? (t.no ?? "—") : "B"}</div>
                    <div className={styles.trackTitle}>
                      {t.title}
                      {/* Rating here changes a number on another album, so the row says so
                          rather than leaving it to be discovered. */}
                      {(t.alsoOn ?? 0) > 0 && (
                        <span className={styles.alsoOn} data-testid="also-on">
                          ALSO ON {t.alsoOn}
                        </span>
                      )}
                    </div>
                    <RatingStrip
                      value={t.rating}
                      live={hover?.id === t.id ? hover.value : null}
                      ground="newsprint"
                      onRate={(n) => rate(t.id, n)}
                      onHover={(n) => setHover(n === null ? null : { id: t.id, value: n })}
                      label={`Rate ${t.title}`}
                    />
                    <div className={styles.scr} data-unrated={t.rating === null ? "true" : undefined}>
                      {t.rating ?? "—"}
                    </div>
                    <div className={styles.dur}>{duration(t.durationMs)}</div>
                    {/* Wrapped so the mobile layout can drop the column: the three-item menu is
                        re-linking work, and re-linking is not something anybody does walking. */}
                    <div className={styles.menuCell}>
                    <RowMenu
                      songId={t.id}
                      title={t.title}
                      isCore={t.isCore}
                      onToggleCore={(isCore) => {
                        // Optimistic, like every other write here: the score block repaints from
                        // this list, and waiting on the network would make the denominator lag.
                        setTracks((prev) => prev.map((x) => (x.id === t.id ? { ...x, isCore } : x)));
                        onToggleCore?.(t.id, isCore);
                      }}
                      loadHistory={loadHistory ?? (async () => [])}
                      loadRecordings={loadRecordings ?? (async () => [])}
                      searchRecordings={searchRecordings}
                      onPointAt={(recordingMbid) => onPointAt?.(t.id, recordingMbid)}
                    />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className={styles.cta}>
          {unratedCore > 0 ? (
            <>
              <button type="button" className={styles.ctaButton} onClick={onEnterBooth}>
                Rate {unratedCore} unrated →
              </button>
              <span className={styles.ctaNote}>OR PRESS R</span>
            </>
          ) : (
            <span className={styles.ctaNote}>ALL {core.length} CORE TRACKS RATED</span>
          )}
        </div>
      </div>
    </div>
  );
}

/** −1.0…+1.0 in 0.1 steps, kept off floating-point drift. */
function clampStep(value: number): number {
  return Math.round(Math.min(1, Math.max(-1, value)) * 10) / 10;
}
